import {
  type Action,
  type Computed,
  FilterActionTypes,
  action,
  computed,
} from "easy-peasy";

import { Logger } from "../../dependencies/logger";
import { type LogEffect, createLogEffect } from "../../utils/store";

const logEffect = (property: keyof FilterActionTypes<NearToModel>) =>
  createLogEffect<NearToModel>("NEAR_ME", property, false);

const logger = new Logger("RESTAURANT_STORE - NEAR_TO", "LOG");
// logger.enable();

export type LocationCoordinates = {
  lat: number;
  lng: number;
};

export type NearToModel = {
  // Base properties
  lat: number | null;
  lng: number | null;
  enabled: boolean;

  // Log effects
  latOnChange: LogEffect<NearToModel>;
  lngOnChange: LogEffect<NearToModel>;
  enabledOnChange: LogEffect<NearToModel>;

  // Actions (setters)
  setLocation: Action<NearToModel, LocationCoordinates>;
  setEnabled: Action<NearToModel, boolean>;
  reset: Action<NearToModel>;

  // Computed properties
  active: Computed<NearToModel, boolean>;
  location: Computed<NearToModel, LocationCoordinates | null>;
};

/**
 * Rounds coordinates to ~50m precision
 * At the equator, 1 degree ≈ 111km, so 0.0005 degrees ≈ 55m
 */
const roundTo50m = (value: number): number =>
  Math.round(value / 0.0005) * 0.0005;

const nearToModel: NearToModel = {
  // Initial state
  lat: null,
  lng: null,
  enabled: false,

  // Log effects
  latOnChange: logEffect("lat"),
  lngOnChange: logEffect("lng"),
  enabledOnChange: logEffect("enabled"),

  // Setters
  setLocation: action((state, payload) => {
    logger.log("setLocation", payload);

    state.lat = payload.lat;
    state.lng = payload.lng;
  }),

  setEnabled: action((state, payload) => {
    logger.log("setEnabled", payload);

    state.enabled = payload;
  }),
  reset: action((state) => {
    logger.log("reset");

    state.lat = null;
    state.lng = null;
    state.enabled = false;
  }),

  // Computed: active when enabled and has valid coordinates
  active: computed(
    (state) => state.enabled && state.lat !== null && state.lng !== null,
  ),

  // Computed: location rounded to 50m precision to reduce unnecessary updates
  location: computed((state) => {
    if (!state.active) {
      return null;
    }

    if (state.lat === null || state.lng === null) {
      return null;
    }

    return {
      lat: roundTo50m(state.lat),
      lng: roundTo50m(state.lng),
    };
  }),
};

export default nearToModel;
